import { z } from 'zod';
import { MANNERS, type BlockRole, type ContentFormat, type Manner } from './content.js';

/**
 * Version of the spoken consent phrase (the phrase itself is in messages).
 * Stored with every voice; bump it when Google changes the phrase.
 */
export const VOICE_CONSENT_VERSION = '2026-09-30';

/** The longest own take, seconds: the Mini App stops recording at it, the API refuses longer ones. */
export const MAX_OWN_TAKE_SECONDS = 150;

export const recordingKindSchema = z.enum(['sample', 'consent']);
export type RecordingKind = z.infer<typeof recordingKindSchema>;

/** What the recording check can find wrong (see apps/api/src/voice/recording-check.ts). */
export const recordingProblemSchema = z.enum([
  'unreadable',
  'too_short',
  'too_long',
  'silent',
  'too_quiet',
  'too_loud',
  'noisy',
  // The engine's align heard something else than the text on the screen.
  'wrong_text',
]);
export type RecordingProblem = z.infer<typeof recordingProblemSchema>;

/** POST /voice/check */
export interface RecordingCheckResult {
  ok: boolean;
  problem: RecordingProblem | null;
  seconds: number;
}

export const voiceProfileStatusSchema = z.enum(['none', 'pending', 'ready']);
export type VoiceProfileStatus = z.infer<typeof voiceProfileStatusSchema>;

/** GET /voice/profile */
export interface VoiceProfileDto {
  status: VoiceProfileStatus;
  createdAt: string | null;
}

/** POST /voice/profile. Expected outcomes come with status 200 and a kind. */
export type VoiceCreateResult =
  | { kind: 'ok'; profile: VoiceProfileDto }
  | { kind: 'recording'; which: RecordingKind; problem: RecordingProblem }
  | { kind: 'consent_mismatch' }
  | { kind: 'limit' }
  | { kind: 'unavailable' };

/** DELETE /voice/profile */
export type VoiceDeleteResult = { kind: 'ok' } | { kind: 'unavailable' };

// --- Voice-overs of approved drafts (stage 4, part 2) ---

/**
 * What the person picks (spec 8.4): a manner of stage 2's list, or 'auto' — the
 * one the brain suggested for the approved text.
 */
export const MANNER_CHOICES = ['auto', ...MANNERS] as const;
export const mannerChoiceSchema = z.enum(MANNER_CHOICES);
export type MannerChoice = z.infer<typeof mannerChoiceSchema>;

/** POST /voiceovers/drafts/:draftId/clone */
export const startVoiceoverSchema = z
  .object({
    manner: mannerChoiceSchema,
    /** "Voice again": a new take even when a ready one exists for this text and manner. */
    fresh: z.boolean().optional(),
  })
  .strict();
export type StartVoiceoverInput = z.infer<typeof startVoiceoverSchema>;

/** POST /voiceovers/drafts/:draftId/stock — a take in a ready-made voice (spec 5.2, 9). */
export const startStockVoiceoverSchema = z
  .object({
    /** Our code of the voice (stock_voices.id). */
    voice: z.string().min(1).max(32),
    manner: mannerChoiceSchema,
    fresh: z.boolean().optional(),
  })
  .strict();
export type StartStockVoiceoverInput = z.infer<typeof startStockVoiceoverSchema>;

/** Which take: a ready-made voice, the person's clone, or their own reading (spec 5.1). */
export type VoiceoverSourceDto = 'stock' | 'clone' | 'own';

export type VoiceoverStatusDto = 'working' | 'ready' | 'failed' | 'rejected';

export type VoiceoverCheckDto = 'clean' | 'minor' | 'gross';

export interface VoiceoverIssueDto {
  blockId: string;
  type: 'missing' | 'extra' | 'substituted';
  expected: string | null;
  heard: string | null;
}

export interface VoiceoverDto {
  id: number;
  source: VoiceoverSourceDto;
  /** stock: our code of the voice; null otherwise. */
  stockVoice: string | null;
  status: VoiceoverStatusDto;
  manner: Manner | null;
  mannerAuto: boolean;
  durationMs: number | null;
  /** voice_mismatch | unavailable | refused | no_voice | interrupted | internal */
  error: string | null;
  /** Where the engine heard something else than the text. */
  issues: VoiceoverIssueDto[];
  /**
   * What the check says (2026-10-05: the person decides, not the check): `minor` — single
   * misheard words, listen and decide; `gross` — the synthesizer lost a piece of the text.
   */
  check: VoiceoverCheckDto;
  /**
   * A signed link (6 h, relative to the API base) to the light copy of the file for
   * listening — `<audio>` streams it; null without a file.
   */
  audioUrl: string | null;
}

/** A ready-made voice on «Озвучка» (spec 5.2). */
export interface StockVoiceDto {
  id: string;
  name: string;
  /** Two words about the sound. */
  about: string;
  /** The sample phrase in each manner: a public link, null while the file is not made. */
  samples: Record<Manner, string | null>;
}

/** GET /voiceovers/drafts/:draftId — everything «Озвучка» needs. */
export interface DraftVoiceDto {
  draftId: number;
  format: ContentFormat;
  blocks: Array<{ id: string; role: BlockRole; text: string }>;
  voice: VoiceProfileStatus;
  /** The manner the brain suggested for this text: what «Само подберёт» plays and voices. */
  manner: Manner | null;
  stock: StockVoiceDto[];
  /** The clone has its manner samples (GET /voice/profile/samples/:manner). */
  cloneSamples: boolean;
  /** Signed links (6 h) to the light copies of the clone's samples; null without samples. */
  cloneSampleUrls: Record<Manner, string> | null;
  /** Clone and stock takes this reel may still start (spec 7); null: no limit. */
  takesLeft: number | null;
  /** The limit itself (setting voiceover_per_draft_limit): «осталось 3 из 5»; null: no limit. */
  takesLimit: number | null;
  /** A take of an earlier text is the newest one: «Текст изменился — послушать заново?». */
  textChanged: boolean;
  /** Ready takes of this exact text, newest first, one per source, voice and manner. */
  ready: VoiceoverDto[];
  /** The take at work for this draft, of any text. */
  working: VoiceoverDto | null;
  /** Newest clone and own takes of this exact text (screens of stage 4). */
  clone: VoiceoverDto | null;
  own: VoiceoverDto | null;
}

export type StartVoiceoverResult =
  | { kind: 'ok'; voiceover: VoiceoverDto }
  /** per_draft: voiceover_per_draft_limit of this reel; daily: daily_voiceover_limit of the person. */
  | { kind: 'limit'; reason: 'per_draft' | 'daily' }
  /** voice_unknown: the stock voice is not on the list (any more). */
  | { kind: 'no_voice' | 'not_reel' | 'not_found' | 'voice_unknown' };

/** POST /voiceovers/drafts/:draftId/own */
export type OwnTakeResult =
  | { kind: 'ok'; voiceover: VoiceoverDto }
  | { kind: 'mismatch'; voiceover: VoiceoverDto }
  | { kind: 'recording'; problem: RecordingProblem }
  /** limit: daily_voiceover_limit — an own take counts toward it (spec 7). */
  | { kind: 'not_reel' | 'not_found' | 'unavailable' | 'limit' };

/** POST /voice/chat: the bot now waits for the sample in the chat; the app opens the chat. */
export interface VoiceChatStartResult {
  botUrl: string | null;
}
